<script setup lang="ts">
import { EMBEDTYPES } from '@/utils/embeds/accountMeta'
import { JDOODLEBLOCKS } from '@/utils/embeds/editorMeta'
import { createApp, ref, computed, onBeforeMount, onMounted, onBeforeUnmount } from 'vue'
import { type IEmbed } from '@/stores/embeds/embedConfigure.store'

import pluginService from '@/services/embeds/plugin.service'
import ideService from '@/services/embeds/ide.service'
import domainManagerService from '@/services/embeds/domainManager.service'
import embedConfigureService from '@/services/embeds/embedConfigure.service'

import languageSelectService from '@/services/embeds/plugin/language.select.service'
import externalLibsService from '@/services/embeds/plugin/external.libs.service'
import stdinArgsService from '@/services/embeds/plugin/stdin.args.service'
import inputArgsService from '@/services/embeds/plugin/input.args.service'
import interactiveToggleService from '@/services/embeds/plugin/interactive.toggle.service'
import inputFilesService from '@/services/embeds/plugin/input.files.service'
import fontsizeService from '@/services/embeds/plugin//font.size.service'
import outputFilesService from '@/services/embeds/plugin/output.files.service'
import exeMatrixService from '@/services/embeds/plugin/exe.matrix.service'

import Recaptcha from '@/components/shared/RecaptchaComp.vue'
import FeaturePermissionMessage from '@/components/embeds/embedSetupConfig/getCode/FeaturePermissionMessage.vue'

import { useEmbedConfigureStore } from '@/stores/embeds/embedConfigure.store'

import { useIdeStore } from '@/stores/embeds/ide.store'

const ideStore = useIdeStore()

const props = defineProps({
  customkey: {
    type: String,
    required: false
  }
})

const embedElement = ref<HTMLElement | null>(null)
const embedData = computed(() => {
  return useEmbedConfigureStore().getEmbed as IEmbed
})

const isShareNotFound = computed(() => {
  return ideStore.shareNotFound
})
const isShareFoundHttpError = computed(() => {
  return ideStore.shareNotFoundHttpError
})
const html = ref<string | null>(null)
const css = computed(() => {
  return embedData.value.config?.css || ''
})
const js = computed(() => {
  return embedData.value.config?.js || ''
})
/**
 * @description inject style
 */
const injectStyle = () => {
  const style = document.createElement('style')
  style.innerHTML = css.value
  embedElement.value?.appendChild(style)
}
/**
 * @description inject script
 */
const injectScript = () => {
  const script = document.createElement('script')
  script.innerHTML = js.value
  embedElement.value?.appendChild(script)
}
/**
 * @description append component
 */
const appendComponent = () => {
  JDOODLEBLOCKS.forEach((block) => {
    const blockElement = document.getElementById(block.id)
    if (blockElement) {
      const vueApp = createApp(block.vueComponent)
      vueApp.mount(blockElement)
    }
  })
}
/**
 * @description post html action
 * @param count - The count
 */
const postHtmlAction = async (count: number = 0) => {
  if (count > 10) {
    return
  }
  if (embedData.value?.config?.html) {
    html.value = embedData.value?.config?.html
  } else {
    await new Promise((resolve) => setTimeout(resolve, 100))
    postHtmlAction(count + 1)
  }
}
/**
 * @description init ide default config
 */
const initIdeDefaultConfig = () => {
  languageSelectService.initLanguageList()
  externalLibsService.initLibs()
  stdinArgsService.initStdinArgus()
  inputArgsService.initInputArgus()
  interactiveToggleService.initInteractiveToggle()
  fontsizeService.initFontSize()
  inputFilesService.initInputFiles()
  outputFilesService.initOutputFiles()
  exeMatrixService.initExeMatrix()
}
onBeforeMount(async () => {
  await pluginService.injectPym()
  ideService.cleanIdeStore()
  ideService.injectAce()
})

onMounted(async () => {
  await domainManagerService
    .checkForValidDomain(props.customkey as string, document.referrer)
    .then(async (result: boolean) => {
      await new Promise((resolve) => setTimeout(resolve, 100))
      await embedConfigureService
        .fetchEmbedById(props.customkey as string)
        .then(async () => {
          if (result === false && embedData.value?.type !== EMBEDTYPES.BASIC) {
            ideStore.setShareNotFound(true)
            ideStore.setShareNotFoundHttpError(
              'This embed is not allowed to be embedded on this domain'
            )
          } else {
            await postHtmlAction()
            initIdeDefaultConfig()
            injectStyle()
            injectScript()
            appendComponent()
            await pluginService.postEmbedPymAction()
          }
        })
        .catch((err) => {
          ideStore.setShareNotFound(true)
          ideStore.setShareNotFoundHttpError(err.message)
        })
    })
    .catch((err) => {
      ideStore.setShareNotFound(true)
      ideStore.setShareNotFoundHttpError(err.message)
    })
})
onBeforeUnmount(() => {
  ideService.ejectAce()
  ideService.cleanIdeStore()
  pluginService.ejectPym()
})
</script>
<template>
  <Recaptcha />
  <FeaturePermissionMessage />
  <p
    v-if="isShareNotFound"
    :class="[
      'print:hidden, p-small ',
      'error',
      'text-center',
      'my-5',
      { hidden: !isShareNotFound }
    ]"
  >
    {{ isShareFoundHttpError || 'Share not found' }}
  </p>

  <div v-else>
    <div v-if="html" ref="embedElement" v-html="html" class="h-full w-full overflow-auto" />
  </div>
</template>
